import { omit } from "lodash";
import { OverlayPanel } from "primereact/overlaypanel";
import { Fragment, useCallback, useRef, useState, type FC, type MouseEvent } from "react";
import styled from "styled-components";

import { useTableDataFilters } from "@ds/hooks";

import { TableFilterOptions } from "@ds/modules/table-data/redux/types";

import { Chip } from "@ds/components/chips";
import { Button, ButtonType } from "@ds/components/forms/buttons";
import { IconType } from "@ds/components/icons";
import { Tooltip } from "@ds/components/tooltips";

import { TableFilterEditor } from "./table-filter-editor";
import { formatTableFilter, formatTableFilterTooltip, type TableFilterDefinition } from "./types";

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const FilterChip = styled(Chip)`
  cursor: pointer;
  border-radius: 8px;
  font-size: 0.9rem;
`;

export const TableFilterBar: FC<
  Pick<TableFilterOptions, "tableType" | "tableTab" | "queryFilters"> & { definitions: TableFilterDefinition[] }
> = ({ tableType, tableTab, queryFilters, definitions }) => {
  const [tableFilters, setTableFilters] = useTableDataFilters(tableType, tableTab, queryFilters);
  const overlayRef = useRef<OverlayPanel>(null);
  const [editing, setEditing] = useState<{ key: number; field?: string }>({ key: 0 });

  const open = useCallback((event: MouseEvent<HTMLElement>, field?: string) => {
    setEditing(({ key }) => ({ key: key + 1, field }));
    overlayRef.current?.show(event, event.currentTarget);
  }, []);

  const close = useCallback(() => overlayRef.current?.hide(), []);

  const onApply = useCallback(
    (field: string, value: TableFilterValue) => {
      setTableFilters({ ...tableFilters, [field]: value });
      close();
    },
    [tableFilters, setTableFilters, close],
  );

  const onRemove = useCallback(
    (event: MouseEvent<HTMLElement>, field: string) => {
      event.stopPropagation();
      setTableFilters(omit(tableFilters, field));
    },
    [tableFilters, setTableFilters],
  );

  const activeDefinitions = definitions.filter(({ field }) => tableFilters[field]);

  return (
    <Wrapper>
      {activeDefinitions.map(definition => {
        const tooltip = formatTableFilterTooltip(definition, tableFilters[definition.field]);
        const chipClass = `table-filter-chip-${definition.field}`;
        return (
          <Fragment key={definition.field}>
            {tooltip && <Tooltip target={`.${chipClass}`} content={tooltip} position="top" showDelay={300} />}
            <FilterChip
              className={chipClass}
              label={formatTableFilter(definition, tableFilters[definition.field])}
              removable
              onClick={event => open(event, definition.field)}
              onRemove={event => onRemove(event, definition.field)}
            />
          </Fragment>
        );
      })}

      <Button label="Filter" icon={IconType.Create} buttonType={ButtonType.Text} onClick={event => open(event)} />

      <OverlayPanel ref={overlayRef}>
        <TableFilterEditor
          key={editing.key}
          definitions={definitions}
          tableFilters={tableFilters}
          initialField={editing.field}
          onApply={onApply}
          onCancel={close}
        />
      </OverlayPanel>
    </Wrapper>
  );
};
